import { useId } from "react";
import Tooltip from "../../utils/Tooltip";

const Input = ({ text, type, list, tooltip, helperText, error, ...rest }) => {
    // One generated id per rendered Input, so the label can point at the
    // control and the control can point back at its helper text.
    const id = useId();
    const helperId = `${id}-helper`;

    // helperText is used for hints as well as errors, so only flag the field
    // as invalid when the caller says it actually is.
    const describedBy = helperText || error ? helperId : undefined;

    const renderElement = () => {
        // Common classes for text-based inputs
        const standardClasses = `bg-gray-50 focus:outline-none focus:shadow-input focus-visible:ring-2 focus-visible:ring-logo-maroon-medium block w-full p-3 text-sm text-gray-900 border border-gray-400 rounded-lg`;

        const a11y = {
            id,
            "aria-describedby": describedBy,
            "aria-invalid": error ? true : undefined,
        };

        switch (type) {
            case "textarea":
                return (
                    <textarea
                        spellCheck={false}
                        {...a11y}
                        {...rest}
                        className={standardClasses}
                    />
                );

            case "select":
            case "select-gender":
                return (
                    <select className={standardClasses} {...a11y} {...rest}>
                        <option value="">{`Select ${text}`}</option>
                        {list.map((element) => (
                            <option key={element.id} value={type === "select" ? element.id : element.value}>
                                {element.name}
                            </option>
                        ))}
                    </select>
                );

            case "checkbox":
                return (
                    <div className="flex items-center h-11"> {/* Height matched to p-3 inputs */}
                        <input
                            type="checkbox"
                            {...a11y}
                            {...rest}
                            className="w-5 h-5 text-blue-600 bg-gray-50 border-gray-400 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <label
                            htmlFor={id}
                            className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-400"
                        >
                            {text} {rest.required && <span className="text-red-500">*</span>}
                        </label>
                        {tooltip && <div className="ml-2"><Tooltip text={tooltip} /></div>}
                    </div>
                );

            default:
                return (
                    <input
                        type={type || "text"}
                        {...a11y}
                        {...rest}
                        className={standardClasses}
                    />
                );
        }
    };

    const helper = (helperText || error) && (
        <p
            id={helperId}
            role={error ? "alert" : undefined}
            className="m-2 text-xs leading-tight text-red-400"
        >
            {error || helperText}
        </p>
    );

    // If it's a checkbox, we render the element directly without the top-level label
    if (type === "checkbox") {
        return (
            <div className="flex-1 mb-6">
                {renderElement()}
                {helper}
            </div>
        );
    }

    return (
        <div className="flex-1 mb-6">
            <label
                htmlFor={id}
                className="dark:text-gray-400 block mb-2 text-sm font-medium text-gray-900"
            >
                <span className="group relative flex items-center gap-2">
                    <span>{text}</span>
                    {rest.required && <span className="text-base leading-none text-red-500">*</span>}
                    {tooltip && <Tooltip text={tooltip} />}
                </span>
            </label>
            {renderElement()}
            {helper}
        </div>
    );
};

export default Input;
